import {
  RULE_BOX_MAX_ITEMS,
  RULE_BOX_MIN_ITEMS,
  type RuleBoxConfig,
  type RuleBoxFacets,
  type RuleBoxPriceMode,
} from "@loot-os/api-contract";
import {
  dollars,
  HIGHEST_MIN_RETURN_PERCENT,
  LOWEST_MIN_RETURN_PERCENT,
  MAX_MARGIN_PERCENT,
  MIN_MARGIN_PERCENT,
  PRICE_MODE_LABEL,
  parseRuleBoxForm,
  type RuleBoxForm,
  type RuleBoxFormField,
  ruleBoxFormFromConfig,
  textChoices,
} from "@loot-os/api-contract/shopifyRuleBoxForm";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@loot-os/ui/components/input-group";
import { Label } from "@loot-os/ui/components/label";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useTRPC } from "../lib/trpc";
import { RuleChoiceField } from "./RuleChoiceField";
import { CategoryFilterFields, OptionFilterFields } from "./RuleFilterGroups";

/**
 * A Shopify rule box's rules, item limits, margin, minimum return and price mode, editable from
 * the box's Shopify tab: the same
 * save as the embedded admin's builder (`shopify.setRuleBoxConfig`), which rebuilds the drop table
 * at once when something it reads changed. Prices are the shop's own, so they are dollars.
 */
export function RuleBoxRulesCard({ boxId, readOnly }: { boxId: string; readOnly: boolean }) {
  const trpc = useTRPC();
  const rule = useQuery(trpc.shopify.ruleBox.queryOptions({ boxId }));
  const facets = useQuery(trpc.shopify.ruleBoxFacets.queryOptions());

  return (
    <Card>
      <CardHeader>
        <CardTitle>Rules</CardTitle>
        <CardDescription>
          Which of the store's products can be inside, and the margin the box keeps. Saving a change
          rebuilds the drop table at once; it is rebuilt from these rules every day too.
        </CardDescription>
      </CardHeader>
      {rule.data ? (
        // Keyed by the saved config, so a save (here or in the Shopify app) resets the form to it.
        <RuleBoxRulesForm
          key={JSON.stringify(rule.data.config)}
          boxId={boxId}
          config={rule.data.config}
          refusalMessage={
            rule.data.refreshStatus === "unavailable" ? rule.data.refusalMessage : null
          }
          facets={facets.data ?? null}
          readOnly={readOnly}
        />
      ) : (
        <CardContent className="text-sm text-muted-foreground">
          {rule.error ? rule.error.message : "Loading the rules…"}
        </CardContent>
      )}
    </Card>
  );
}

function RuleBoxRulesForm({
  boxId,
  config,
  refusalMessage,
  facets,
  readOnly,
}: {
  boxId: string;
  config: RuleBoxConfig;
  refusalMessage: string | null;
  facets: RuleBoxFacets | null;
  readOnly: boolean;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const saved = ruleBoxFormFromConfig(config);
  const [form, setForm] = useState<RuleBoxForm>(saved);
  const [errors, setErrors] = useState<Partial<Record<RuleBoxFormField, string>>>({});
  const edit = (patch: Partial<RuleBoxForm>) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setErrors({});
  };
  const dirty = JSON.stringify(form) !== JSON.stringify(saved);

  const save = useMutation(
    trpc.shopify.setRuleBoxConfig.mutationOptions({
      onSuccess: (result) => {
        const { preview } = result;
        if (!result.refreshed) toast.success("Rules saved.");
        else if (preview.ok) {
          toast.success(
            `Rules saved. The box now sells at ${dollars(preview.priceCents)} with ${preview.entries.length} products.`,
          );
        } else {
          toast.warning(`Rules saved, but the box can't be rebuilt: ${preview.message}`);
        }
        void queryClient.invalidateQueries({ queryKey: trpc.shopify.ruleBox.pathKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.shopify.boxHistory.pathKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.shopify.boxChannels.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.boxes.get.pathKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.boxes.list.pathKey() });
      },
    }),
  );

  const submit = () => {
    const parsed = parseRuleBoxForm(form);
    if (!parsed.ok) {
      setErrors(parsed.errors);
      return;
    }
    save.mutate({ boxId, config: parsed.config });
  };

  const disabled = readOnly || save.isPending;
  return (
    <>
      <CardContent className="flex flex-col gap-6">
        {refusalMessage && (
          <p className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            The last rebuild failed: {refusalMessage} The box shows sold out on Shopify until a
            rebuild succeeds.
          </p>
        )}
        <div className="grid gap-6 md:grid-cols-2">
          <RuleChoiceField
            id="rule-include-tags"
            label="Products tagged"
            description="A product with any one of these tags qualifies. Leave empty for any tag."
            noun="tags"
            options={textChoices(facets?.tags ?? [])}
            chosen={form.includeTags}
            freeText
            disabled={disabled}
            onChange={(includeTags) => edit({ includeTags })}
          />
          <RuleChoiceField
            id="rule-exclude-tags"
            label="Leave out products tagged"
            description="A product with any one of these tags never goes in."
            noun="tags"
            options={textChoices(facets?.tags ?? [])}
            chosen={form.excludeTags}
            freeText
            disabled={disabled}
            onChange={(excludeTags) => edit({ excludeTags })}
          />
          <RuleChoiceField
            id="rule-vendors"
            label="Vendors"
            description="Only products from these vendors. Leave empty for any vendor."
            noun="vendors"
            options={textChoices(facets?.vendors ?? [])}
            chosen={form.vendors}
            freeText={false}
            disabled={disabled}
            onChange={(vendors) => edit({ vendors })}
          />
          <RuleChoiceField
            id="rule-product-types"
            label="Product types"
            description="Only products of these types. Leave empty for any type."
            noun="product types"
            options={textChoices(facets?.productTypes ?? [])}
            chosen={form.productTypes}
            freeText={false}
            disabled={disabled}
            onChange={(productTypes) => edit({ productTypes })}
          />
          <RuleChoiceField
            id="rule-collections"
            label="Collections"
            description="A product in any one of these collections qualifies. Leave empty for any collection."
            noun="collections"
            options={facets?.collections ?? []}
            chosen={form.collections}
            freeText={false}
            disabled={disabled}
            missingLabel="Deleted collection"
            onChange={(collections) => edit({ collections })}
          />
          <RuleChoiceField
            id="rule-locations"
            label="Fulfillment locations"
            description={
              facets && !facets.locationsNamed && facets.locations.length === 0
                ? "The merchant allows location access from the box builder in the Shopify app."
                : "Only products stocked at one of these locations, the store's own or a fulfillment app's. Leave empty for any location."
            }
            noun="locations"
            options={(facets?.locations ?? []).map((l) => ({
              id: l.id,
              label: l.fulfillmentService ? `${l.label} (${l.fulfillmentService})` : l.label,
            }))}
            chosen={form.locations}
            freeText={false}
            disabled={disabled}
            missingLabel="Closed location"
            onChange={(locations) => edit({ locations })}
          />
          <CategoryFilterFields
            facets={facets}
            filters={form.categoryAttributes}
            disabled={disabled}
            onChange={(categoryAttributes) => edit({ categoryAttributes })}
          />
          <OptionFilterFields
            facets={facets}
            filters={form.options}
            disabled={disabled}
            onChange={(options) => edit({ options })}
          />
          <DollarsField
            id="rule-min-price"
            label="Lowest product price"
            description="Leave empty for no minimum."
            value={form.minPrice}
            error={errors.minPrice}
            disabled={disabled}
            onChange={(minPrice) => edit({ minPrice })}
          />
          <DollarsField
            id="rule-max-price"
            label="Highest product price"
            description="Leave empty for no maximum."
            value={form.maxPrice}
            error={errors.maxPrice}
            disabled={disabled}
            onChange={(maxPrice) => edit({ maxPrice })}
          />
          <CountField
            id="rule-min-items"
            label="Fewest items"
            description="With fewer matching products in stock, the box shows sold out. Leave empty for no minimum."
            placeholder={String(RULE_BOX_MIN_ITEMS)}
            value={form.minItems}
            error={errors.minItems}
            disabled={disabled}
            onChange={(minItems) => edit({ minItems })}
          />
          <CountField
            id="rule-max-items"
            label="Most items"
            description="With more matching products, a spread of them by price is kept. Leave empty for no maximum."
            placeholder={String(RULE_BOX_MAX_ITEMS)}
            value={form.maxItems}
            error={errors.maxItems}
            disabled={disabled}
            onChange={(maxItems) => edit({ maxItems })}
          />
          <div className="flex flex-col gap-2">
            <Label htmlFor="rule-margin">Margin</Label>
            <InputGroup className="max-w-40">
              <InputGroupInput
                id="rule-margin"
                inputMode="decimal"
                value={form.marginPercent}
                disabled={disabled}
                aria-invalid={errors.marginPercent ? true : undefined}
                onChange={(e) => edit({ marginPercent: e.currentTarget.value })}
              />
              <InputGroupAddon align="inline-end">
                <InputGroupText>%</InputGroupText>
              </InputGroupAddon>
            </InputGroup>
            <FieldNote
              error={errors.marginPercent}
              description={`What the box keeps on average, ${MIN_MARGIN_PERCENT}% to ${MAX_MARGIN_PERCENT}% of its price.`}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="rule-min-return">Least any item is worth</Label>
            <InputGroup className="max-w-40">
              <InputGroupInput
                id="rule-min-return"
                inputMode="decimal"
                placeholder={String(HIGHEST_MIN_RETURN_PERCENT)}
                value={form.minReturnPercent}
                disabled={disabled}
                aria-invalid={errors.minReturnPercent ? true : undefined}
                onChange={(e) => edit({ minReturnPercent: e.currentTarget.value })}
              />
              <InputGroupAddon align="inline-end">
                <InputGroupText>%</InputGroupText>
              </InputGroupAddon>
            </InputGroup>
            <FieldNote
              error={errors.minReturnPercent}
              description={`Of the box price, ${LOWEST_MIN_RETURN_PERCENT}% to ${HIGHEST_MIN_RETURN_PERCENT}%; empty is ${HIGHEST_MIN_RETURN_PERCENT}%. Under 70% lets the products fill every rarity tier at fixed odds.`}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label>Price</Label>
            <div className="flex flex-wrap items-center gap-2">
              {(["auto", "fixed"] as const satisfies readonly RuleBoxPriceMode[]).map((mode) => (
                <Button
                  key={mode}
                  type="button"
                  size="sm"
                  variant={form.priceMode === mode ? "secondary" : "ghost"}
                  aria-pressed={form.priceMode === mode}
                  disabled={disabled}
                  onClick={() => edit({ priceMode: mode })}
                >
                  {PRICE_MODE_LABEL[mode]}
                </Button>
              ))}
              {form.priceMode === "fixed" && (
                <InputGroup className="max-w-36">
                  <InputGroupAddon>
                    <InputGroupText>$</InputGroupText>
                  </InputGroupAddon>
                  <InputGroupInput
                    aria-label="Fixed price"
                    inputMode="decimal"
                    value={form.fixedPrice}
                    disabled={disabled}
                    aria-invalid={errors.fixedPrice ? true : undefined}
                    onChange={(e) => edit({ fixedPrice: e.currentTarget.value })}
                  />
                </InputGroup>
              )}
            </div>
            <FieldNote
              error={errors.fixedPrice}
              description={
                form.priceMode === "auto"
                  ? "A store-friendly price estimated from the products, held while they support it."
                  : "The box sells at this price; a rebuild fails if the products can't support it."
              }
            />
          </div>
        </div>
      </CardContent>
      {!readOnly && (
        <CardFooter className="justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={!dirty || save.isPending}
            onClick={() => {
              setForm(saved);
              setErrors({});
            }}
          >
            Discard
          </Button>
          <Button type="button" disabled={!dirty || save.isPending} onClick={submit}>
            {save.isPending ? "Saving…" : "Save rules"}
          </Button>
        </CardFooter>
      )}
    </>
  );
}

function DollarsField({
  id,
  label,
  description,
  value,
  error,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  description: string;
  value: string;
  error: string | undefined;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      <InputGroup className="max-w-40">
        <InputGroupAddon>
          <InputGroupText>$</InputGroupText>
        </InputGroupAddon>
        <InputGroupInput
          id={id}
          inputMode="decimal"
          value={value}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          onChange={(e) => onChange(e.currentTarget.value)}
        />
      </InputGroup>
      <FieldNote error={error} description={description} />
    </div>
  );
}

/** A whole number of items, empty for no limit. */
function CountField({
  id,
  label,
  description,
  placeholder,
  value,
  error,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  description: string;
  placeholder: string;
  value: string;
  error: string | undefined;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      <InputGroup className="max-w-40">
        <InputGroupInput
          id={id}
          inputMode="numeric"
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          onChange={(e) => onChange(e.currentTarget.value)}
        />
        <InputGroupAddon align="inline-end">
          <InputGroupText>items</InputGroupText>
        </InputGroupAddon>
      </InputGroup>
      <FieldNote error={error} description={description} />
    </div>
  );
}

/** A field's help line, replaced by its error while it has one. */
function FieldNote({ error, description }: { error: string | undefined; description: string }) {
  return error ? (
    <p className="text-sm text-destructive">{error}</p>
  ) : (
    <p className="text-sm text-muted-foreground">{description}</p>
  );
}
